import React, {forwardRef, useCallback, useMemo} from 'react';
import {
    Link,
    LinkProps,
    matchRoutes,
    Navigate,
    NavigateOptions,
    resolvePath,
    To,
    useLocation,
    useNavigate
} from "react-router-dom";
import {FilterRouteObject, useRoutesWithProducts} from "../config/routes";
import {QueryFilter} from "./DataTable/Filters/types";

interface PreserveQueryDestinationArgs {
    currentPathname: string,
    currentSearch: string,
    routes: FilterRouteObject[],
    to: To
}

interface MergePreservedQueryToArgs {
    currentSearch: string,
    to: To,
    preserveCohorts?: boolean,
    preserveCourses?: boolean
}

export const mergePreservedQueryTo = ({
    currentSearch,
    to,
    preserveCohorts = false,
    preserveCourses = false
}: MergePreservedQueryToArgs): To => {
    const nextParams = new URLSearchParams(typeof to === 'string' ? '' : to.search);
    const currentParams = new URLSearchParams(currentSearch);
    let hasChanged = false;

    const preserveQueryParam = (enabled: boolean, key: QueryFilter) => {
        const value = currentParams.get(key);
        if (!enabled || !value || nextParams.has(key)) {
            return;
        }

        nextParams.set(key, value);
        hasChanged = true;
    };

    preserveQueryParam(preserveCohorts, QueryFilter.COHORTS);
    preserveQueryParam(preserveCourses, QueryFilter.COURSES);

    if (!hasChanged) {
        return to;
    }

    const search = nextParams.toString();
    if (typeof to === 'string') {
        const separator = to.includes('?') ? '&' : '?';
        return `${to}${separator}${search}`;
    }

    return {
        ...to,
        search: search ? `?${search}` : ''
    };
};

export const getPreservedQueryTo = ({
    currentPathname,
    currentSearch,
    routes,
    to
}: PreserveQueryDestinationArgs): To => {
    const resolvedTo = resolvePath(to, currentPathname);
    const match = matchRoutes(routes, {pathname: resolvedTo.pathname})?.at(0);

    if (!match?.route) {
        return to;
    }

    const route = match.route as FilterRouteObject;
    if (!route.cohortFilter && !route.courseFilter) {
        return to;
    }

    return mergePreservedQueryTo({
        currentSearch,
        to: resolvedTo,
        preserveCohorts: !!route.cohortFilter,
        preserveCourses: !!route.courseFilter
    });
};

export const PreserveQueryLink = forwardRef<HTMLAnchorElement, LinkProps>(({to, ...props}, ref) => {
    const location = useLocation();
    const routes = useRoutesWithProducts();

    const preservedTo = useMemo(() => getPreservedQueryTo({
        currentPathname: location.pathname,
        currentSearch: location.search,
        routes,
        to
    }), [location.pathname, location.search, routes, to]);

    return <Link {...props} ref={ref} to={preservedTo}/>;
});

PreserveQueryLink.displayName = 'PreserveQueryLink';

export const usePreserveQueryNavigate = () => {
    const location = useLocation();
    const routes = useRoutesWithProducts();
    const navigate = useNavigate();

    return useCallback((to: To, options?: NavigateOptions) => {
        to  = getPreservedQueryTo({
            currentPathname: location.pathname,
            currentSearch: location.search,
            routes,
            to
        });
        navigate(to, options);
    }, [location.pathname, location.search, navigate, routes]);
};

interface PreserveQueryNavigateProps {
    to: To,
    preserveCohorts?: boolean,
    preserveCourses?: boolean,
    replace?: boolean
}

export const PreserveQueryNavigate = ({
    to,
    preserveCohorts = false,
    preserveCourses = false,
    replace
}: PreserveQueryNavigateProps) => {
    const location = useLocation();

    const preservedTo = useMemo(() => mergePreservedQueryTo({
        currentSearch: location.search,
        to,
        preserveCohorts,
        preserveCourses
    }), [location.search, preserveCohorts, preserveCourses, to]);

    return <Navigate to={preservedTo} replace={replace}/>;
};
